import { ethers } from "ethers";
import { MARKET_ABI } from "../../../contracts/contracts";

export const PAGE_SIZE = 25;
export const ALLOWED_COLLECTIONS = [
  "0x3055c5595915522760e810d5630c0ecf295f9e16",
  "0xb0ba1dbc7137ec56b120a5690249a2be3cb96abb",
];
export const EXCLUDED_COLLECTIONS = [
  "0xd8c8a2e8661bfd5347e77443d3321827cad3220f",
];
export const SUBGRAPH_URL = process.env.REACT_APP_GRAPH_URL;
export const MARKET_ADDRESS_V1 = process.env.REACT_APP_MARKETPLACE_ADDRESS_V1;
export const MARKET_ADDRESS_V2 = process.env.REACT_APP_MARKETPLACE_ADDRESS_V2;
export const MARKET_ADDRESS = MARKET_ADDRESS_V2;
const metadataCache = new Map();
const collectionsCache = new Map();
const graphQLCache = new Map();
const pendingRequests = new Map();

export const resolveIPFS = (uri) => {
  if (!uri) return "";
  if (uri.startsWith("ipfs://")) {
    const gateway =
      process.env.REACT_APP_PINATA_GATEWAY || "https://gateway.pinata.cloud";
    return uri.replace("ipfs://", `${gateway}/ipfs/`);
  }
  return uri;
};

const fetchWithRetry = async (
  url,
  options = {},
  retries = 3,
  backoff = 1000,
) => {
  try {
    const response = await fetch(url, options);

    if (response.status === 429 && retries > 0) {
      console.warn(`Rate limited. Retrying in ${backoff}ms...`);
      await new Promise((resolve) => setTimeout(resolve, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }

    return response;
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
    throw error;
  }
};

const fetchGraphQL = async (query, subgraphUrl = SUBGRAPH_URL) => {
  const cacheKey = JSON.stringify(query);

  if (graphQLCache.has(cacheKey)) return graphQLCache.get(cacheKey);
  if (pendingRequests.has(cacheKey)) return pendingRequests.get(cacheKey);

  const requestPromise = (async () => {
    try {
      const response = await fetchWithRetry(subgraphUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });

      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);

      graphQLCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error("GraphQL error:", error);
      return { data: null };
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

const fetchWithCache = async (url) => {
  if (metadataCache.has(url)) {
    return metadataCache.get(url);
  }

  try {
    const response = await fetchWithRetry(url);
    const data = await response.json();
    metadataCache.set(url, data);
    return data;
  } catch (error) {
    return null;
  }
};
export const fetchCollections = async (subgraphUrl = SUBGRAPH_URL) => {
  const cacheKey = `all:${subgraphUrl}`;
  if (collectionsCache.has(cacheKey)) {
    return collectionsCache.get(cacheKey);
  }

  const query = `
    {
      collections(first: 100) {
        id
        name
        description
        contractURI          
        tokens { id }
      }
    }
  `;

  const { data } = await fetchGraphQL(query, (subgraphUrl = SUBGRAPH_URL));
  const allCollections = data?.collections || [];
  const mintedCollections = allCollections.filter((c) => c.tokens.length > 0);

  const collections = await Promise.all(
    mintedCollections.map(async (col) => {
      let image = "";

      if (col.contractURI) {
        try {
          const uri = resolveIPFS(col.contractURI);
          const metaData = await fetchWithCache(uri);
          image = resolveIPFS(metaData?.image || "");
        } catch (e) {
          console.warn("Failed to load metadata for:", col.id);
        }
      }

      return {
        id: col.id.toLowerCase(),
        name: col.name || "Unnamed Collection",
        description: col.description || "No description available",
        image,
        count: col.tokens?.length || 0,
      };
    }),
  );

  collectionsCache.set(cacheKey, collections);
  return collections;
};

export const fetchListingsFromCollection = async (
  collectionAddress,
  subgraphUrl = SUBGRAPH_URL,
) => {
  if (!collectionAddress) return [];

  const cacheKey = `listings-${collectionAddress.toLowerCase()}:${subgraphUrl}`;
  const listingsCache = new Map();

  if (listingsCache.has(cacheKey)) {
    return listingsCache.get(cacheKey);
  }

  let allListings = [];
  let skip = 0;
  let hasMore = true;
  let retries = 0;
  const MAX_RETRIES = 3;

  while (hasMore && retries < MAX_RETRIES) {
    const query = `
      {
        listings(
          first: ${PAGE_SIZE}, 
          skip: ${skip}, 
          orderBy: id, 
          orderDirection: asc,
          where: { 
            collection: "${collectionAddress.toLowerCase()}", 
            active: true 
          }
        ) {
          id
          pricePerToken
          quantity
          active
          token {
            id
            tokenId
            metadataUri
            collection {
              id
              name
              description
              contractURI
            }
          }
        }
      }
    `;

    try {
      const { data, errors } = await fetchGraphQL(
        query,
        (subgraphUrl = SUBGRAPH_URL),
      );

      if (errors) {
        retries++;
        continue;
      }

      const listings = data?.listings || [];

      if (!listings.length) {
        hasMore = false;
        break;
      }
      const listingsWithMeta = await Promise.all(
        listings.map(async (listing) => {
          try {
            const metadataUri = resolveIPFS(listing.token.metadataUri);
            let meta = metadataCache.get(metadataUri);

            if (!meta) {
              meta = await fetchWithCache(metadataUri);
              if (meta) {
                metadataCache.set(metadataUri, meta);
              }
            }

            if (!meta) {
              return null;
            }

            const collectionId = listing.token.collection.id.toLowerCase();
            let collectionMeta = metadataCache.get(
              `collection-${collectionId}`,
            );

            if (
              !collectionMeta &&
              listing.token.collection.contractMetadataUri
            ) {
              const collectionUri = resolveIPFS(
                listing.token.collection.contractMetadataUri,
              );
              collectionMeta = await fetchWithCache(collectionUri);
              if (collectionMeta) {
                metadataCache.set(`collection-${collectionId}`, collectionMeta);
              }
            }

            return {
              id: listing.id,
              name: meta.name || `Token #${listing.token.tokenId}`,
              desc: meta.description || "",
              img: resolveIPFS(meta.image),
              prev: resolveIPFS(meta.preview || meta.image),
              year:
                meta.year ||
                meta.attributes?.find((a) => a.trait_type === "Year")?.value ||
                "Unknown",
              size:
                meta.size ||
                meta.attributes?.find((a) => a.trait_type === "Size")?.value ||
                "N/A",
              price: (Number(listing.pricePerToken) / 1e18).toFixed(4),
              collection:
                listing.token.collection.name || "Unknown Collection1",
              collectionMeta: collectionMeta || {
                image: "",
                desc: listing.token.collection.description || "",
              },
              token: listing.token,
              pricePerToken: listing.pricePerToken,
              quantity: Number(listing.quantity) || 1,
            };
          } catch (error) {
            return null;
          }
        }),
      );

      const validListings = listingsWithMeta.filter(Boolean);
      allListings.push(...validListings);

      if (listings.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        skip += PAGE_SIZE;
      }

      retries = 0;
    } catch (error) {
      retries++;
      if (retries >= MAX_RETRIES) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
    }
  }

  listingsCache.set(cacheKey, allListings);
  return allListings;
};

export const buyNFT = async (
  listingId,
  quantity,
  pricePerToken,
  ethAddress,
  provider,
) => {
  if (!provider) throw new Error("Wallet not connected");

  const signer = await provider.getSigner();
  const contract = new ethers.Contract(MARKET_ADDRESS, MARKET_ABI, signer);

  const priceWei = ethers.toBigInt(pricePerToken);
  const totalPrice = priceWei * ethers.toBigInt(quantity);

  const balance = await provider.getBalance(ethAddress);
  if (balance < totalPrice) throw new Error("Not enough ETH to buy NFT");

  const tx = await contract.buy(listingId, Number(quantity), {
    value: totalPrice,
  });
  await tx.wait();
};

export const GET_COLLECTIONS = `
  query GetCollections {
    collections(first: 100) {
      id
      name
      description
      contractURI
      tokens(first: 5) {
        id
        tokenId
        metadataUri
      }
    }
  }
`;

export const ACTIVE_LISTINGS_ALL = `
  query GetAllActiveListings {
    listings(
      where: { active: true }
      first: 100
      orderBy: createdAt
      orderDirection: desc
    ) {
      id
      listingId
      seller
      pricePerToken
      quantity
      token {
        id
        tokenId
        metadataUri
        collection {
          id
          name
          description
          contractURI
        }
      }
    }
  }
`;

export const ACTIVE_LISTINGS = `
  query GetActiveListings($market: Bytes!, $first: Int, $skip: Int) {
    listings(
      where: { active: true, market: $market }
      first: $first
      skip: $skip
      orderBy: createdAt
      orderDirection: desc
    ) {
      id
      listingId
      seller
      pricePerToken
      quantity
      market
       token {
            id
            tokenId
            metadataUri
            collection {
              id
              name
              description
              contractURI
            }
          }
    }
  }
`;

export const GET_LISTINGS_WITH_MARKET = `
  query GetListingsWithMarket {
    listings(
      where: { active: true }
      first: 1000
      orderBy: createdAt
      orderDirection: desc
    ) {
      market
      quantity
      token {
        collection {
          id
          name
          description
          contractURI
        }
      }
    }
  }
`;

export const GET_ALL_COLLECTIONS = `
  query GetAllCollections {
    collections(first: 200) {
      id
      name
      description
      contractURI
      totalListedQuantity
    }
  }
`;

export const findRelatedCollection = (collections, listing) => {
  return collections?.find((col) => col.name === listing.nft?.name) || null;
};

export const formatPrice = (price) => {
  try {
    return ethers.formatEther(price);
  } catch (err) {
    console.error("Error formatting price:", err);
    return price;
  }
};

export const getNFTImage = async (metadataUri) => {
  try {
    const uri = resolveIPFS(metadataUri);
    const meta = await fetchWithCache(uri);
    return resolveIPFS(meta?.image || "");
  } catch {
    return "";
  }
};

export const getNFTMetadata = async (metadataUri) => {
  try {
    const uri = resolveIPFS(metadataUri);
    const meta = await fetchWithCache(uri);

    return {
      name: meta?.name || "",
      description: meta?.description || "",
      image: resolveIPFS(meta?.image || ""),
      preview: resolveIPFS(meta?.preview || ""),
      attributes: meta?.attributes || [],
      raw: meta,
    };
  } catch {
    return null;
  }
};

export const getCollectionMetadata = async (contractMetadataUri) => {
  try {
    const uri = resolveIPFS(contractMetadataUri);
    const meta = await fetchWithCache(uri);

    return {
      name: meta?.name || "",
      description: meta?.description || "",
      image: resolveIPFS(meta?.image || ""),
      preview: resolveIPFS(meta?.preview || ""),
      keywords: meta?.keywords || [],
      raw: meta,
    };
  } catch {
    return null;
  }
};
